import React, { useState, useEffect } from "react";

const IncomeTaxCalculator = () => {
  const [annualIncome, setAnnualIncome] = useState(1200000);
  const [section80CDeduction, setSection80CDeduction] = useState(150000);
  const [section80DDeduction, setSection80DDeduction] = useState(25000);
  const [homeLoanInterest, setHomeLoanInterest] = useState(200000);
  const [otherDeductions, setOtherDeductions] = useState(50000);
  const [taxRegime, setTaxRegime] = useState("new");
  const [age, setAge] = useState("below60");
  const [periodType, setPeriodType] = useState("annual");
  const [result, setResult] = useState({
    grossIncome: "0.00",
    totalDeductions: "0.00",
    taxableIncome: "0.00",
    incomeTax: "0.00",
    surcharge: "0.00",
    cess: "0.00",
    totalTax: "0.00",
    netIncome: "0.00",
    effectiveTaxRate: "0.00",
    monthlyTax: "0.00",
    taxBracket: "",
    taxSavingTip: "",
  });

  // Income Tax Calculation Function
  const calculateIncomeTax = () => {
    const income = parseFloat(annualIncome);
    const sec80C = parseFloat(section80CDeduction);
    const sec80D = parseFloat(section80DDeduction);
    const homeLoan = parseFloat(homeLoanInterest);
    const otherDed = parseFloat(otherDeductions);

    if (isNaN(income) || income < 0) return;

    const grossIncome = income;
    let totalDeductions = 0;
    let taxableIncome = income;
    let incomeTax = 0;

    // New Tax Regime (No deductions except standard deduction)
    if (taxRegime === "new") {
      // New regime doesn't allow most deductions
      totalDeductions = 0;
      taxableIncome = income - 50000; // Standard deduction of 50k
      
      // New Tax Regime Slabs (FY 2023-24)
      if (taxableIncome <= 300000) {
        incomeTax = 0;
      } else if (taxableIncome <= 600000) {
        incomeTax = (taxableIncome - 300000) * 0.05;
      } else if (taxableIncome <= 900000) {
        incomeTax = 15000 + (taxableIncome - 600000) * 0.10;
      } else if (taxableIncome <= 1200000) {
        incomeTax = 45000 + (taxableIncome - 900000) * 0.15;
      } else if (taxableIncome <= 1500000) {
        incomeTax = 90000 + (taxableIncome - 1200000) * 0.20;
      } else {
        incomeTax = 150000 + (taxableIncome - 1500000) * 0.30;
      }
    } else {
      // Old Tax Regime (With deductions)
      totalDeductions = Math.min(sec80C, 150000) + sec80D + Math.min(homeLoan, 200000) + otherDed;
      taxableIncome = income - totalDeductions - 50000; // Standard deduction

      // Basic exemption limit based on age
      let exemptionLimit = 250000;
      if (age === "60to80") exemptionLimit = 300000;
      if (age === "above80") exemptionLimit = 500000;

      taxableIncome = Math.max(0, taxableIncome - exemptionLimit);

      // Old Tax Regime Slabs
      if (taxableIncome <= 250000) {
        incomeTax = taxableIncome * 0.05;
      } else if (taxableIncome <= 500000) {
        incomeTax = 12500 + (taxableIncome - 250000) * 0.10;
      } else if (taxableIncome <= 1000000) {
        incomeTax = 37500 + (taxableIncome - 500000) * 0.20;
      } else {
        incomeTax = 137500 + (taxableIncome - 1000000) * 0.30;
      }
    }

    // Rebate under Section 87A
    if (taxableIncome <= 500000 && taxRegime === "new") {
      incomeTax = Math.max(0, incomeTax - 25000);
    } else if (taxableIncome <= 500000 && taxRegime === "old") {
      incomeTax = Math.max(0, incomeTax - 12500);
    }

    // Surcharge calculation
    let surcharge = 0;
    if (income > 5000000 && income <= 10000000) {
      surcharge = incomeTax * 0.10;
    } else if (income > 10000000 && income <= 20000000) {
      surcharge = incomeTax * 0.15;
    } else if (income > 20000000 && income <= 50000000) {
      surcharge = incomeTax * 0.25;
    } else if (income > 50000000) {
      surcharge = incomeTax * 0.37;
    }

    // Health and Education Cess (4%)
    const cess = (incomeTax + surcharge) * 0.04;
    const totalTax = incomeTax + surcharge + cess;
    const netIncome = grossIncome - totalTax;
    const effectiveTaxRate = (totalTax / grossIncome) * 100;
    const monthlyTax = totalTax / 12;

    let taxBracket = "";
    let taxSavingTip = "";

    if (totalTax === 0) {
      taxBracket = "Tax Free";
      taxSavingTip = "No tax liability - You're in the exempted bracket";
    } else if (effectiveTaxRate < 10) {
      taxBracket = "Low Tax Bracket";
      taxSavingTip = "Consider maximizing 80C deductions for additional savings";
    } else if (effectiveTaxRate < 20) {
      taxBracket = "Moderate Tax Bracket";
      taxSavingTip = "Explore NPS, health insurance for tax optimization";
    } else if (effectiveTaxRate < 30) {
      taxBracket = "High Tax Bracket";
      taxSavingTip = "Maximize all deductions and consider tax-saving instruments";
    } else {
      taxBracket = "Highest Tax Bracket";
      taxSavingTip = "Consult a tax advisor for advanced tax planning strategies";
    }

    setResult({
      grossIncome: grossIncome.toFixed(2),
      totalDeductions: totalDeductions.toFixed(2),
      taxableIncome: (grossIncome - totalDeductions).toFixed(2),
      incomeTax: incomeTax.toFixed(2),
      surcharge: surcharge.toFixed(2),
      cess: cess.toFixed(2),
      totalTax: totalTax.toFixed(2),
      netIncome: netIncome.toFixed(2),
      effectiveTaxRate: effectiveTaxRate.toFixed(2),
      monthlyTax: monthlyTax.toFixed(2),
      taxBracket,
      taxSavingTip,
    });
  };

  useEffect(() => {
    calculateIncomeTax();
  }, []);

  return (
    <div className="calculator-wrapper">
      <div className="calculator-container">
        {/* Header */}
        <div className="calculator-header">
          <div className="header-icon">
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: 'white'}}>
              <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
              <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
            </svg>
          </div>
          <h1>Income Tax Calculator (India)</h1>
          <p>
            Calculate your income tax liability for FY 2023-24 under old and new tax regimes
          </p>
        </div>

        {/* Main Calculator Card */}
        <div className="calculator-card">
          <div className="calculator-grid">
            {/* Left Side - Input Section */}
            <div className="input-section">
              <h2 className="section-title">
                <svg className="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="1" x2="12" y2="23"></line>
                  <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                </svg>
                Income & Deduction Details
              </h2>

              {/* Annual Income */}
              <div className="input-group">
                <label>Annual Gross Income</label>
                <div className="input-wrapper">
                  <span className="input-prefix">₹</span>
                  <input
                    type="number"
                    value={annualIncome}
                    onChange={(e) => setAnnualIncome(e.target.value)}
                    className="calc-input"
                    placeholder="1200000"
                  />
                </div>
                <div className="quick-select-group">
                  {[500000, 1000000, 1500000, 2500000].map((amount) => (
                    <button
                      key={amount}
                      onClick={() => setAnnualIncome(amount)}
                      className="quick-select-btn primary"
                    >
                      ₹{amount >= 100000 ? `${(amount / 100000).toFixed(0)}L` : `${(amount / 1000).toFixed(0)}K`}
                    </button>
                  ))}
                </div>
              </div>

              {/* Tax Regime */}
              <div className="input-group">
                <label>Tax Regime</label>
                <div className="quick-select-group">
                  {[
                    { value: "new", label: "New Regime" },
                    { value: "old", label: "Old Regime" }
                  ].map((type) => (
                    <button
                      key={type.value}
                      onClick={() => setTaxRegime(type.value)}
                      className={`quick-select-btn ${taxRegime === type.value ? 'primary' : 'secondary'}`}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Age Category */}
              <div className="input-group">
                <label>Age Category</label>
                <div className="quick-select-group">
                  {[
                    { value: "below60", label: "Below 60" },
                    { value: "60to80", label: "60-80" },
                    { value: "above80", label: "Above 80" }
                  ].map((type) => (
                    <button
                      key={type.value}
                      onClick={() => setAge(type.value)}
                      className={`quick-select-btn ${age === type.value ? 'success' : 'secondary'}`}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Section 80C Deduction */}
              {taxRegime === "old" && (
                <div className="input-group">
                  <label>Section 80C Deduction (Max ₹1.5L)</label>
                  <div className="input-wrapper">
                    <span className="input-prefix">₹</span>
                    <input
                      type="number"
                      value={section80CDeduction}
                      onChange={(e) => setSection80CDeduction(e.target.value)}
                      className="calc-input"
                      placeholder="150000"
                    />
                  </div>
                  <div className="quick-select-group">
                    {[50000, 100000, 150000].map((amount) => (
                      <button
                        key={amount}
                        onClick={() => setSection80CDeduction(amount)}
                        className="quick-select-btn success"
                      >
                        ₹{(amount / 1000).toFixed(0)}K
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Section 80D Deduction */}
              {taxRegime === "old" && (
                <div className="input-group">
                  <label>Section 80D (Health Insurance)</label>
                  <div className="input-wrapper">
                    <span className="input-prefix">₹</span>
                    <input
                      type="number"
                      value={section80DDeduction}
                      onChange={(e) => setSection80DDeduction(e.target.value)}
                      className="calc-input"
                      placeholder="25000"
                    />
                  </div>
                  <div className="quick-select-group">
                    {[0, 25000, 50000, 100000].map((amount) => (
                      <button
                        key={amount}
                        onClick={() => setSection80DDeduction(amount)}
                        className="quick-select-btn secondary"
                      >
                        {amount === 0 ? "₹0" : `₹${(amount / 1000).toFixed(0)}K`}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Home Loan Interest */}
              {taxRegime === "old" && (
                <div className="input-group">
                  <label>Home Loan Interest (Max ₹2L)</label>
                  <div className="input-wrapper">
                    <span className="input-prefix">₹</span>
                    <input
                      type="number"
                      value={homeLoanInterest}
                      onChange={(e) => setHomeLoanInterest(e.target.value)}
                      className="calc-input"
                      placeholder="200000"
                    />
                  </div>
                  <div className="quick-select-group">
                    {[0, 100000, 200000].map((amount) => (
                      <button
                        key={amount}
                        onClick={() => setHomeLoanInterest(amount)}
                        className="quick-select-btn success"
                      >
                        {amount === 0 ? "₹0" : `₹${(amount / 100000).toFixed(0)}L`}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Other Deductions */}
              {taxRegime === "old" && (
                <div className="input-group">
                  <label>Other Deductions (80E, 80G, etc.)</label>
                  <div className="input-wrapper">
                    <span className="input-prefix">₹</span>
                    <input
                      type="number"
                      value={otherDeductions}
                      onChange={(e) => setOtherDeductions(e.target.value)}
                      className="calc-input"
                      placeholder="50000"
                    />
                  </div>
                  <div className="quick-select-group">
                    {[0, 25000, 50000, 100000].map((amount) => (
                      <button
                        key={amount}
                        onClick={() => setOtherDeductions(amount)}
                        className="quick-select-btn secondary"
                      >
                        {amount === 0 ? "₹0" : `₹${(amount / 1000).toFixed(0)}K`}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <button onClick={calculateIncomeTax} className="calculate-btn">
                Calculate Tax
              </button>
            </div>

            {/* Right Side - Results Section */}
            <div className="result-section">
              <h2 className="section-title">
                <svg className="section-icon" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <circle cx="12" cy="12" r="6"></circle>
                  <circle cx="12" cy="12" r="2"></circle>
                </svg>
                Tax Calculation
              </h2>

              <div className="result-card">
                {/* Total Tax - Highlighted */}
                <div className="result-item highlight">
                  <div className="result-item-header">
                    <span className="result-label">Total Tax Payable</span>
                    <div className="result-icon-wrapper white">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: 'white'}}>
                        <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
                        <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.totalTax).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    {result.taxBracket} ({result.effectiveTaxRate}% effective rate)
                  </div>
                </div>

                {/* Income Tax */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Income Tax</span>
                    <div className="result-icon-wrapper blue">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#3b82f6'}}>
                        <line x1="12" y1="1" x2="12" y2="23"></line>
                        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.incomeTax).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    Base tax amount
                  </div>
                </div>

                {/* Net Take Home */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Net Take Home Income</span>
                    <div className="result-icon-wrapper purple">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#9333ea'}}>
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.netIncome).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    After tax deduction
                  </div>
                </div>

                {/* Monthly Tax */}
                <div className="result-item">
                  <div className="result-item-header">
                    <span className="result-label">Monthly Tax (TDS)</span>
                    <div className="result-icon-wrapper green">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{color: '#10b981'}}>
                        <rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
                        <line x1="1" y1="10" x2="23" y2="10"></line>
                      </svg>
                    </div>
                  </div>
                  <div className="result-value">
                    ₹{Number(result.monthlyTax).toLocaleString("en-IN")}
                  </div>
                  <div className="result-subtext">
                    Per month deduction
                  </div>
                </div>

                {/* Visual Breakdown */}
                <div className="breakdown-section">
                  <h3 className="breakdown-title">Income Distribution</h3>
                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">Gross Income</span>
                      <span className="breakdown-percentage">
                        100% (₹{Number(result.grossIncome).toLocaleString("en-IN")})
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill green"
                        style={{
                          width: "100%",
                        }}
                      />
                    </div>
                  </div>
                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">Total Tax</span>
                      <span className="breakdown-percentage">
                        {result.effectiveTaxRate}% (₹{Number(result.totalTax).toLocaleString("en-IN")})
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill blue"
                        style={{
                          width: `${result.effectiveTaxRate}%`,
                        }}
                      />
                    </div>
                  </div>
                  <div className="breakdown-item">
                    <div className="breakdown-header">
                      <span className="breakdown-label">Net Income</span>
                      <span className="breakdown-percentage">
                        {(100 - parseFloat(result.effectiveTaxRate)).toFixed(2)}% (₹{Number(result.netIncome).toLocaleString("en-IN")})
                      </span>
                    </div>
                    <div className="breakdown-bar">
                      <div
                        className="breakdown-bar-fill green"
                        style={{
                          width: `${100 - parseFloat(result.effectiveTaxRate)}%`,
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Tax Saving Tip Box */}
                <div className="breakdown-section" style={{marginTop: '1.5rem', padding: '1rem', background: '#f0fdf4', borderRadius: '8px', border: '1px solid #10b981'}}>
                  <h3 className="breakdown-title" style={{color: '#065f46', marginBottom: '0.5rem'}}>Tax Saving Tip</h3>
                  <p style={{fontSize: '0.875rem', color: '#065f46', lineHeight: '1.5', margin: 0}}>
                    {result.taxSavingTip}
                    {taxRegime === "old" && parseFloat(result.totalDeductions) > 0 && 
                      ` You're saving ₹${Number(result.totalDeductions).toLocaleString("en-IN")} through deductions.`}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Info Section */}
        <div className="info-section">
          <div className="info-header">
            <div className="info-icon-wrapper">
              <svg className="info-icon" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="16" x2="12" y2="12"></line>
                <line x1="12" y1="8" x2="12.01" y2="8"></line>
              </svg>
            </div>
            <div>
              <h3>Understanding Income Tax in India</h3>
              <p>
                Income Tax in India is levied by the Central Government on income earned during a financial year (April 
                1 to March 31). From FY 2020-21, taxpayers can choose between two tax regimes: the Old Regime (with 
                deductions and exemptions) and the New Regime (with lower tax rates but limited deductions). The Old 
                Regime allows deductions under sections like 80C (₹1.5 lakh for EPF, PPF, LIC, etc.), 80D (health 
                insurance), 24(b) (home loan interest up to ₹2 lakh), and others. The New Regime offers simplified 
                lower tax slabs but doesn't allow most deductions except standard deduction of ₹50,000. Tax is 
                calculated on taxable income (gross income minus deductions and exemptions). Additional charges include 
                surcharge (for high incomes above ₹50 lakh) and Health & Education Cess at 4%. TDS (Tax Deducted at 
                Source) is deducted monthly by employers and must be reconciled when filing returns.
              </p>
            </div>
          </div>

          <div className="formula-section">
            <h4>Tax Calculation Formula:</h4>
            <div className="formula-display">
              Total Tax = Income Tax + Surcharge + Health & Education Cess (4%)
            </div>

            <div className="formula-params">
              <div className="formula-param">
                <div className="formula-param-key">Taxable Income</div>
                <div className="formula-param-value">Gross Income - Deductions - Exemptions</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">Income Tax</div>
                <div className="formula-param-value">Based on tax slab rates (5%, 10%, 15%, 20%, 30%)</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">Surcharge</div>
                <div className="formula-param-value">Additional tax for income above ₹50 lakh</div>
              </div>
              <div className="formula-param">
                <div className="formula-param-key">Cess</div>
                <div className="formula-param-value">4% on (Income Tax + Surcharge)</div>
              </div>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="faq-section">
            <h3>Frequently Asked Questions</h3>

            <div className="faq-item">
              <h5>Which tax regime should I choose - Old or New?</h5>
              <p>
                Choose the Old Regime if you have significant deductions (80C, home loan, insurance). Choose the New Regime if you have minimal deductions and prefer lower tax rates with simpler filing.
              </p>
            </div>

            <div className="faq-item">
              <h5>What are the main deductions under Section 80C?</h5>
              <p>
                Section 80C allows up to ₹1.5 lakh deduction for investments in PPF, EPF, life insurance premiums, ELSS mutual funds, NSC, 5-year tax-saving FDs, principal repayment on home loans, and tuition fees.
              </p>
            </div>

            <div className="faq-item">
              <h5>What is the standard deduction for salaried employees?</h5>
              <p>
                Salaried employees get a standard deduction of ₹50,000 from gross salary in both old and new tax regimes. This is a flat deduction available to all salaried individuals.
              </p>
            </div>

            <div className="faq-item">
              <h5>How is surcharge calculated on income tax?</h5>
              <p>
                Surcharge is levied on income tax: 10% for income ₹50L-₹1Cr, 15% for ₹1Cr-₹2Cr, 25% for ₹2Cr-₹5Cr, and 37% above ₹5Cr. It applies only on the tax amount, not total income.
              </p>
            </div>

            <div className="faq-item">
              <h5>What is Section 80D and who can claim it?</h5>
              <p>
                Section 80D allows deduction for health insurance premiums: up to ₹25,000 for self/family (₹50,000 if senior citizen), plus additional ₹25,000 (₹50,000 for senior) for parents. Only available in old regime.
              </p>
            </div>

            <div className="faq-item">
              <h5>When should I file my income tax return?</h5>
              <p>
                ITR filing deadline is July 31 for individuals (not requiring audit) for the previous financial year. For FY 2023-24 (AY 2024-25), file by July 31, 2024. Late filing attracts penalties.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IncomeTaxCalculator;